import { SaveOutlined } from '@ant-design/icons'
import { useCan } from '@salescore/client-common'
import type { ViewConfigKpiPivot } from '@salescore/core'
import type { ModalFuncProps } from 'antd'
import { Alert, Button, Form, Input, message, Space } from 'antd'
import { t } from 'i18next'
import { useState } from 'react'
import { useRecoilValue } from 'recoil'

import { viewsContextAtom } from '../../../recoil/view/atoms'

interface BuildKpiPivotFormModalPropertiesArguments {
  onCreate: (viewId: string) => void
}

export const buildKpiPivotFormModalProperties = ({
  onCreate,
}: BuildKpiPivotFormModalPropertiesArguments): ModalFuncProps => ({
  icon: null,
  title: t(`ダッシュボードの新規作成`),
  width: '80%',
  style: {
    maxWidth: 1000,
    padding: 0,
  },
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  footer: null,
  content: (
    <ModalContent
      onCreate={(viewId) => {
        onCreate(viewId)
        void message.success(t(`ダッシュボードを作成しました`))
      }}
    />
  ),
})

interface FormValue {
  name: string
}

const ModalContent = ({ onCreate }: { onCreate: (viewId: string) => void }) => {
  const [isSaving, setIsSaving] = useState(false)
  const { createView } = useRecoilValue(viewsContextAtom)
  const canCreateKpiPivot = useCan(`kpi-pivot-create`)

  return (
    <>
      {!canCreateKpiPivot && (
        <Alert className="mb-4" showIcon type="warning" message={t(`権限がないため作成できません`)} />
      )}
      <Form<FormValue>
        wrapperCol={{ span: 8 }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onFinish={async (value) => {
          const config: ViewConfigKpiPivot = {
            type: 'kpiPivot',
            kpis: [],
            presets: [],
          }

          try {
            setIsSaving(true)
            const result = await createView({
              // viewGroupId: kpiPivotViewGroupId, // 指定しないことで、デフォルトのルート配下で作成される
              name: value.name,
              config,
            })
            if (result === undefined) {
              // eslint-disable-next-line sonarjs/todo-tag
              void message.error(t(`エラーが発生しました。`)) // TODO: 発生しないはず？
              return
            }

            onCreate(result.id)
          } catch {
            void message.error(t(`エラーが発生しました。`))
          } finally {
            setIsSaving(false)
          }
        }}
        initialValues={{}}
      >
        <Form.Item name={'name'} label={t(`ダッシュボード名`)} rules={[{ required: true }]}>
          <Input width={200} />
        </Form.Item>

        <Space className="flex flex-row-reverse">
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={!canCreateKpiPivot}
            >
              {t(`保存`)}
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </>
  )
}
